import { useEffect, useMemo } from "react";

import { SUPPORT_EMAIL } from "@/constants/common.constants";
import {
  DEFAULT_PLATFORM_TITLE,
  DEFAULT_PRIMARY_COLOR,
  useBrandColorContext,
} from "@/contexts/BrandColorContext";
import { env } from "@/env";
import { useGetPlatformQuery } from "@/graphql/__generated__/graphql";

import { DomainBrandingContext } from "./DomainBrandingContext";

const currentHost = window.location.host;
const domainPlatformId = env.domainPlatformMap[currentHost];

export const DomainBrandingProvider = ({ children }: { children: React.ReactNode }) => {
  const {
    setBrandColor,
    setTitle,
    setLightLogo,
    setDarkLogo,
    setLightEmblem,
    setDarkEmblem,
    setFavicon,
    setSupportEmail,
    setIsWhiteLabeled,
    setIsBrandingLoading,
  } = useBrandColorContext();

  const { data, loading, error } = useGetPlatformQuery({
    variables: { id: Number(domainPlatformId) },
    skip: !domainPlatformId,
  });

  // Guest routes have no CurrentPlatformContext — keep theme aligned with domain platform.
  // When authenticated, CurrentPlatformProvider owns brand color.
  useEffect(() => {
    setIsBrandingLoading(!!domainPlatformId && loading);
  }, [loading, setIsBrandingLoading]);

  useEffect(() => {
    if (!domainPlatformId) {
      setBrandColor(DEFAULT_PRIMARY_COLOR);
      setTitle(DEFAULT_PLATFORM_TITLE);
      setLightLogo(null);
      setDarkLogo(null);
      setLightEmblem(null);
      setDarkEmblem(null);
      setFavicon(null);
      setSupportEmail(SUPPORT_EMAIL);
      setIsWhiteLabeled(false);
      return;
    }

    if (!data?.platform || error) {
      return;
    }

    const platform = data.platform;

    setBrandColor(platform.primaryColor || DEFAULT_PRIMARY_COLOR);
    setTitle(platform.title || DEFAULT_PLATFORM_TITLE);
    setLightLogo(platform.lightLogo || null);
    setDarkLogo(platform.darkLogo || null);
    setLightEmblem(platform.lightEmblem || null);
    setDarkEmblem(platform.darkEmblem || null);
    setFavicon(platform.favicon || null);
    setSupportEmail(platform.companyEmail || SUPPORT_EMAIL);
    setIsWhiteLabeled(true);
  }, [
    data?.platform,
    error,
    setBrandColor,
    setDarkEmblem,
    setDarkLogo,
    setFavicon,
    setIsWhiteLabeled,
    setLightEmblem,
    setLightLogo,
    setSupportEmail,
    setTitle,
  ]);

  const contextValue = useMemo(
    () => ({
      loading: !!domainPlatformId && loading,
      isError: !!error,
    }),
    [loading, error]
  );

  return (
    <DomainBrandingContext.Provider value={contextValue}>{children}</DomainBrandingContext.Provider>
  );
};
